import React, { useState, useEffect } from 'react';
import { Gauge, TrendingUp, MessageCircle, Target, Flame } from 'lucide-react';
import { apiClient } from '../../api/client';

const ReadinessScoreCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.request('/api/enhancements/readiness-score')
      .then(setData).catch(() => {});
  }, []);

  if (!data) return null;

  const colorMap = {
    green: { ring: 'text-emerald-500', bg: 'bg-emerald-50', label: 'text-emerald-700' },
    blue: { ring: 'text-blue-500', bg: 'bg-blue-50', label: 'text-blue-700' },
    yellow: { ring: 'text-amber-500', bg: 'bg-amber-50', label: 'text-amber-700' },
    gray: { ring: 'text-gray-400', bg: 'bg-gray-50', label: 'text-gray-600' },
  };
  const colors = colorMap[data.color] || colorMap.gray;
  const pct = data.total_score;
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (pct / 100) * circumference;

  const breakdownIcons = {
    streak: <Flame size={14} className="text-orange-500" />,
    mood: <TrendingUp size={14} className="text-pink-500" />,
    challenges: <Target size={14} className="text-blue-500" />,
    conversations: <MessageCircle size={14} className="text-purple-500" />,
  };

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid="readiness-score-card">
      <div className="flex items-center gap-2 mb-4">
        <Gauge size={20} className="text-purple-500" />
        <h3 className="font-bold text-gray-900">Readiness Score</h3>
      </div>
      <div className="flex items-center gap-6">
        {/* Circular gauge */}
        <div className="relative flex-shrink-0">
          <svg width="100" height="100" viewBox="0 0 100 100" className="-rotate-90">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#f3f4f6" strokeWidth="8" />
            <circle
              cx="50" cy="50" r="40" fill="none"
              stroke="currentColor" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={offset}
              className={`${colors.ring} transition-all duration-1000`}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-gray-900">{pct}</span>
            <span className="text-[10px] text-gray-500 uppercase font-medium">{data.level}</span>
          </div>
        </div>

        {/* Breakdown */}
        <div className="flex-1 space-y-2">
          {Object.entries(data.breakdown).map(([key, b]) => (
            <div key={key} className="flex items-center gap-2">
              {breakdownIcons[key]}
              <div className="flex-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">{b.label}</span>
                  <span className="font-semibold text-gray-800">{b.score}/{b.max}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full mt-0.5">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      b.score >= 20 ? 'bg-emerald-400' : b.score >= 12 ? 'bg-blue-400' : b.score >= 6 ? 'bg-amber-400' : 'bg-gray-300'
                    }`}
                    style={{ width: `${(b.score / b.max) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReadinessScoreCard;
